import { useForm } from "@inertiajs/react";
import { type FormEvent, useEffect, useRef, useState } from "react";

import backgroundRed from "@/assets/background_red.svg";
import AuthCard from "@/components/AuthCard";
import Button from "@/components/Button";
import CourseSummaryCard from "@/components/CourseSummaryCard";
import { enrollments, waitLists } from "@/routes";
import type { Course } from "@/types";

type DietOption = { value: string; label: string };

type Props = {
  course: Course;
  diet_options: DietOption[];
  /** Course is full but has its waitlist enabled: the form enrolls into the
   *  waitlist instead. */
  waitlist: boolean;
  restriction_reason?: string;
};

const DEFAULT_DIET = "no_diet";

export default function NewForStudent({
  course,
  diet_options,
  waitlist,
  restriction_reason: restrictionReason,
}: Props) {
  const form = useForm<{ diet: string[] }>({ diet: [DEFAULT_DIET] });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (waitlist) {
      form.transform((data) => ({
        wait_list: { course_id: course.id, diet: data.diet },
      }));
      form.post(waitLists.create().url);
    } else {
      form.transform((data) => ({
        enrollment: { course_id: course.id, diet: data.diet },
      }));
      form.post(enrollments.create().url);
    }
  }

  return (
    <AuthCard
      title={waitlist ? "Inscríbete en la lista de espera" : "Inscripción"}
      subtitle={
        waitlist
          ? "El curso no tiene cupos disponibles, pero puedes quedar en la lista de espera y te avisaremos si se libera un cupo."
          : undefined
      }
      background={backgroundRed}
    >
      <form onSubmit={submit} className="flex flex-col gap-6">
        <CourseSummaryCard course={course} />

        {restrictionReason ? (
          <p className="self-center rounded-2xl border-2 border-deep-red/40 bg-deep-red/5 px-4 py-3 text-center font-sans text-sm text-deep-red">
            {restrictionReason}
          </p>
        ) : (
          <>
            <DietSelect
              options={diet_options}
              value={form.data.diet}
              onChange={(diet) => form.setData("diet", diet)}
            />

            <Button
              type="submit"
              disabled={form.processing}
              className="self-center"
            >
              {waitlist
                ? "Inscribirme en lista de espera"
                : "Confirmar inscripción"}
            </Button>
          </>
        )}
      </form>
    </AuthCard>
  );
}

/** Pill dropdown with the diet options. Only one option can be selected at a
 *  time. */
function DietSelect({
  options,
  value,
  onChange,
}: {
  options: DietOption[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  function select(option: string) {
    onChange([option]);
  }

  const isDefault = value.length === 1 && value[0] === DEFAULT_DIET;
  const summary = options
    .filter((o) => value.includes(o.value))
    .map((o) => o.label)
    .join(", ");

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-full border border-ink px-5 py-3 text-left font-sans text-sm font-light text-ink"
      >
        <span className="truncate">
          {isDefault
            ? "Modifica tu dieta (solo en caso de ser necesario)"
            : summary}
        </span>
        <ChevronIcon
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <ul className="absolute z-10 mt-2 w-full rounded-2xl border border-ink/30 bg-white py-2 shadow-md">
          {options.map((option) => (
            <li key={option.value}>
              <label className="flex cursor-pointer items-center gap-3 px-5 py-2 font-sans text-sm font-light text-ink hover:bg-ink/5">
                <input
                  type="radio"
                  name="diet"
                  checked={value.includes(option.value)}
                  onChange={() => select(option.value)}
                  className="h-4 w-4 accent-steel"
                />
                {option.label}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
    </svg>
  );
}
